import React, { useState, useEffect } from 'react';
import { Star, Loader2, Send, CheckCircle, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { BusinessThemeProvider, ThemedHeading, ThemedText, ThemedButton } from '@/components/theme/BusinessThemeProvider';
import { trackFunnelStep, generateSessionId } from '@/lib/analytics/funnel-tracking';

interface AIReviewFormProps {
  formId: string;
  businessId: string;
  businessName: string;
  starThreshold: number;
  googlePlaceId?: string;
  uiTextConfig?: {
    ratingTitle?: string;
    ratingSubtitle?: string;
    successTitle?: string;
    successMessageHigh?: string;
    successMessageLow?: string;
  };
}

type FormStep = 'rating' | 'question' | 'success';

export const AIReviewForm: React.FC<AIReviewFormProps> = ({
  formId,
  businessId,
  businessName,
  starThreshold,
  googlePlaceId,
  uiTextConfig,
}) => {
  const [step, setStep] = useState<FormStep>('rating');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [sessionId] = useState(() => generateSessionId());

  // AI Question state
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [questionText, setQuestionText] = useState<string>('');
  const [questionTopic, setQuestionTopic] = useState<string>('');
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);

  // Response state
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Generate AI question after rating
  const generateQuestion = async () => {
    setLoadingQuestion(true);
    setQuestionError(null);

    try {
      const { data, error } = await supabase.functions.invoke('generate-review-question', {
        body: { businessId, formId, sessionId }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to generate question');

      setQuestionId(data.questionId);
      setQuestionText(data.questionText);
      setQuestionTopic(data.questionTopic);

      // Track funnel step
      trackFunnelStep({
        businessId,
        formId,
        step: 'ai_question_shown',
        sessionId,
        rating,
        metadata: { questionTopic: data.questionTopic },
      });

    } catch (err) {
      console.error('Error generating question:', err);
      // Use fallback question
      setQuestionText("What stood out most during your visit today?");
      setQuestionTopic('general');
      setQuestionError('Using fallback question');
    } finally {
      setLoadingQuestion(false);
    }
  };

  const handleRatingSelect = async (selectedRating: number) => {
    setRating(selectedRating);

    // Track rating
    trackFunnelStep({
      businessId,
      formId,
      step: 'rating_selected',
      sessionId,
      rating: selectedRating,
    });

    // For high ratings, redirect to Google Reviews immediately
    if (selectedRating >= starThreshold && googlePlaceId) {
      // Track Google redirect
      trackFunnelStep({
        businessId,
        formId,
        step: 'google_redirect',
        sessionId,
        rating: selectedRating,
      });

      // Small delay for visual feedback
      await new Promise(resolve => setTimeout(resolve, 500));
      window.location.href = `https://search.google.com/local/writereview?placeid=${googlePlaceId}`;
      return;
    }

    // For low ratings, generate AI question
    setStep('question');
    generateQuestion();
  };

  const handleSubmit = async () => {
    if (!response.trim()) {
      toast.error('Please share your feedback');
      return;
    }

    setSubmitting(true);

    try {
      // Save response to ai_generated_questions table
      if (questionId) {
        await supabase
          .from('ai_generated_questions')
          .update({
            response_text: response.trim(),
            response_rating: rating,
            responded_at: new Date().toISOString(),
          })
          .eq('id', questionId);
      }

      // Also save as a regular review for analytics
      const { data: reviewData } = await supabase
        .from('reviews')
        .insert({
          business_id: businessId,
          form_id: formId,
          rating,
          content: response.trim(),
          session_id: sessionId,
          source: 'ai_form',
        })
        .select('id')
        .single();

      // Trigger real-time feedback analysis (fire and forget)
      supabase.functions.invoke('analyze-feedback-realtime', {
        body: {
          reviewId: reviewData?.id,
          questionId,
          businessId,
          content: response.trim(),
          rating,
          questionTopic,
        }
      }).catch(err => console.warn('Analysis failed:', err));

      // Track completion
      trackFunnelStep({
        businessId,
        formId,
        step: 'ai_response_submitted',
        sessionId,
        rating,
        metadata: { questionTopic },
      });

      setStep('success');

    } catch (err) {
      console.error('Error submitting response:', err);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const processText = (text: string) => {
    return text.replace('{businessName}', businessName);
  };

  return (
    <BusinessThemeProvider businessId={businessId}>
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="w-full max-w-md">
          {/* Rating Step */}
          {step === 'rating' && (
            <div className="text-center space-y-6 animate-in fade-in duration-500">
              <div className="space-y-2">
                <ThemedHeading level={1} className="text-2xl font-bold">
                  {processText(uiTextConfig?.ratingTitle || 'Rate your experience')}
                </ThemedHeading>
                <ThemedText muted>
                  {processText(uiTextConfig?.ratingSubtitle || `with ${businessName}`)}
                </ThemedText>
              </div>

              <div className="flex justify-center gap-2 py-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleRatingSelect(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-2 transition-transform hover:scale-110 active:scale-95"
                  >
                    <Star
                      className={`h-12 w-12 transition-colors ${
                        star <= (hoveredRating || rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>

              <ThemedText muted className="text-sm">
                Tap a star to continue
              </ThemedText>
            </div>
          )}

          {/* Question Step */}
          {step === 'question' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
              {loadingQuestion ? (
                <div className="text-center py-12 space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                    <Brain className="h-8 w-8 text-primary animate-pulse" />
                  </div>
                  <ThemedText muted>Preparing your question...</ThemedText>
                </div>
              ) : (
                <>
                  {/* Rating Badge */}
                  <div className="flex justify-center">
                    <div className="flex items-center gap-1 px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                      {[...Array(rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>

                  {/* Question */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border">
                    <ThemedHeading level={2} className="text-xl font-semibold text-center">
                      {questionText}
                    </ThemedHeading>
                  </div>

                  {/* Response Input */}
                  <div className="space-y-3">
                    <Textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="Share your thoughts..."
                      className="min-h-[120px] resize-none text-base"
                      autoFocus
                    />
                    <ThemedButton
                      onClick={handleSubmit}
                      disabled={submitting || !response.trim()}
                      className="w-full"
                      variant="accent"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Submit Feedback
                        </>
                      )}
                    </ThemedButton>
                  </div>

                  {questionError && (
                    <ThemedText muted className="text-xs text-center">
                      {questionError}
                    </ThemedText>
                  )}
                </>
              )}
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="text-center space-y-6 animate-in fade-in zoom-in duration-500">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>

              <div className="space-y-2">
                <ThemedHeading level={1} className="text-2xl font-bold">
                  {uiTextConfig?.successTitle || 'Thank You!'}
                </ThemedHeading>
                <ThemedText muted>
                  {rating >= starThreshold
                    ? (uiTextConfig?.successMessageHigh || 'We appreciate your feedback!')
                    : (uiTextConfig?.successMessageLow || 'Your feedback helps us improve.')
                  }
                </ThemedText>
              </div>
            </div>
          )}
        </div>
      </div>
    </BusinessThemeProvider>
  );
};

export default AIReviewForm;
